/**
 * Cybersecurity Awareness Training Slides
 * Audience: Well-educated SME professionals (non-technical)
 * See MODULE.md for slide data format
 */

export const cybersecuritySlides = [
  // ─── Block 1: Foundation ───────────────────────────────────
  {
    title: "Welcome to Cybersecurity Training",
    content: "Cyberattacks don't just target large corporations. According to the 2025 Verizon Data Breach Investigations Report, small and mid-sized businesses account for a growing share of confirmed breaches — and recovery costs can be devastating, often exceeding what an SME can absorb. This training gives you practical habits to protect your clients, your firm, and yourself.",
    icon: "ShieldCheck"
  },
  {
    title: "Your Digital Identity",
    content: "Every account you use is a door into your business. Reusing passwords is like using the same key for your office, home, and car — if one is stolen, everything is exposed. A password manager stores unique, strong passwords for you so you only need to remember one.",
    icon: "KeyRound",
    tips: [
      "Use a password manager (1Password, Bitwarden) — it remembers so you don't have to",
      "Create passphrases instead of passwords: 'River$Lamp!Tuesday42' is strong and memorable",
      "Never reuse passwords — especially between personal and work accounts",
      "If a service you use announces a breach, change that password immediately"
    ]
  },
  {
    title: "Multi-Factor Authentication",
    content: "A password alone isn't enough anymore. Multi-factor authentication (MFA) adds a second step — like a code on your phone — so even if someone steals your password, they still can't get in. Think of it as a deadbolt on top of your regular lock.",
    icon: "Shield",
    tips: [
      "Use an authenticator app (Microsoft Authenticator, Google Authenticator) — it's more secure than SMS codes",
      "Enable MFA on email, banking, cloud storage, and any tool with client data",
      "Save your backup/recovery codes in a secure place in case you lose your phone",
      "If a service offers MFA and you haven't turned it on, do it today"
    ]
  },

  // ─── Block 2: Recognizing Threats ──────────────────────────
  {
    title: "Phishing: Beyond Obvious Scams",
    content: "Modern phishing doesn't look like a Nigerian prince email. It looks like a DocuSign request from a client, a password reset from Microsoft, or an invoice from a vendor you actually use. Attackers study your business relationships to make their messages convincing.",
    icon: "Fish",
    tips: [
      "Check the sender's actual email address, not just the display name — 'John Smith' might be sending from john@amaz0n-support.com",
      "Be suspicious of any unexpected request involving money, passwords, or file downloads",
      "When in doubt, contact the sender through a different channel (call them, use a known email)",
      "Hover over links before clicking — does the URL match where you expect to go?"
    ]
  },
  {
    title: "Business Email Compromise",
    content: "Imagine receiving an email from your CEO: 'I need you to wire $15,000 to this new vendor urgently — I'm in a meeting and can't talk.' This is Business Email Compromise, the most costly cyber threat to SMEs. Attackers impersonate executives, clients, or vendors to trick you into transferring money or sharing sensitive data.",
    icon: "Briefcase",
    tips: [
      "Always verify financial requests through a phone call to a known number — never use contact info from the suspicious email itself",
      "Establish a verification protocol for any payment changes, wire transfers, or new bank details",
      "Watch for subtle urgency and secrecy: 'Handle this quietly' or 'Don't mention this to anyone yet'",
      "A legitimate colleague will never be upset that you took 5 minutes to verify a large transaction"
    ]
  },
  {
    title: "AI-Powered Scams & Deepfakes",
    content: "Attackers now use AI to clone voices from a few seconds of audio and create realistic fake video calls. A finance director in Hong Kong was tricked into transferring $25 million after a video call with deepfake versions of colleagues. If you can't verify it's real, don't act on it.",
    icon: "Bot",
    tips: [
      "Be skeptical of unexpected voice or video calls requesting urgent action — especially involving money",
      "Establish a verbal code word with key colleagues for verifying identity in critical situations",
      "AI-written phishing emails no longer have spelling mistakes — perfect grammar doesn't mean it's legitimate",
      "If a client or executive asks for something unusual over video, hang up and call them back directly"
    ]
  },
  {
    title: "Social Engineering at Work",
    content: "Social engineering exploits trust, not technology. Someone calls claiming to be from your IT provider and asks for remote access. A 'new employee' asks to borrow your laptop for a minute. A visitor tailgates through the secure door. These attacks work because professionals are naturally helpful and polite.",
    icon: "UserX",
    tips: [
      "Verify identity before granting access to systems, information, or physical spaces — it's not rude, it's professional",
      "Be cautious of urgency and authority tactics: 'The CEO needs this right now' or 'I'm from the auditing firm'",
      "Never share credentials, even with IT support — legitimate IT will never ask for your password",
      "At conferences or networking events, be mindful of what you share about your company's systems and processes"
    ]
  },
  {
    title: "Suspicious Links, Files & QR Codes",
    content: "A client sends a Google Drive link to a shared document. A restaurant has a QR code for the menu. A vendor sends a PDF invoice. These are everyday actions — and exactly what attackers exploit. The question isn't 'is this suspicious?' but 'was I expecting this?'",
    icon: "Link2",
    tips: [
      "If you weren't expecting a file or link, verify with the sender before opening — even if it looks legitimate",
      "QR codes in public places can be tampered with — check that a sticker hasn't been placed over the original",
      "Before entering credentials on any page, check the URL carefully — 'microsoft-login.com' is not 'microsoft.com'",
      "When in doubt, navigate to the website directly rather than clicking the link in the email"
    ]
  },

  // ─── Block 3: Safe Daily Habits ────────────────────────────
  {
    title: "Using AI Tools Safely",
    content: "AI tools like ChatGPT and Claude are powerful productivity boosters — but anything you type into them may be stored on external servers and could be accessed or reviewed by the service provider. Pasting a client contract, financial data, or personal information into a public AI tool is like reading it aloud in a crowded room.",
    icon: "Brain",
    tips: [
      "Never paste client data, contracts, personal information, or financial details into public AI tools",
      "Use your company's approved AI tools when available — they often have data protection agreements in place",
      "Anonymize data before using AI: replace real names, numbers, and identifiers with placeholders",
      "Remember: AI outputs can be wrong — always verify facts, figures, and legal references before using them"
    ]
  },
  {
    title: "Cloud Sharing & Collaboration",
    content: "Google Drive, Dropbox, OneDrive, and SharePoint make sharing easy — sometimes too easy. A document shared with 'Anyone with the link' is accessible to anyone on the internet, including search engines. One careless sharing setting can expose client files to the world.",
    icon: "Cloud",
    tips: [
      "Share files with specific people by email, not with 'Anyone with the link'",
      "Review sharing permissions on sensitive folders quarterly — remove access for people who no longer need it",
      "Before sharing, check the file for hidden metadata (author names, tracked changes, comments)",
      "Use expiring links when sharing documents with external parties"
    ]
  },
  {
    title: "Working Remotely & On the Go",
    content: "Your home office, the airport lounge, and the coffee shop are all extensions of your workplace — but without the same protections. Public Wi-Fi networks can be monitored, shoulder surfers can read your screen, and leaving a device unattended for 30 seconds is all an attacker needs.",
    icon: "Home",
    tips: [
      "Use a VPN whenever you're on public Wi-Fi — it encrypts your connection so others can't see your traffic",
      "Use a privacy screen filter on your laptop when working in public spaces",
      "Secure your home Wi-Fi: change the default router password and use WPA3 encryption",
      "Lock your screen every time you step away — even at home if others have access to your workspace"
    ]
  },
  {
    title: "Your Phone Is a Business Tool",
    content: "Your phone likely has your work email, client contacts, cloud files, and authenticator app. If it's lost or compromised, it's not just a personal inconvenience — it's a business data breach. Treat your phone with the same care as your laptop.",
    icon: "Smartphone",
    tips: [
      "Enable biometric lock (face/fingerprint) and a strong PIN — avoid simple patterns",
      "Turn on 'Find My Device' so you can remotely locate or wipe a lost phone",
      "Review app permissions regularly — does that flashlight app really need access to your contacts?",
      "Keep work and personal apps separate when possible — consider a work profile on Android"
    ]
  },
  {
    title: "Software Updates & Unapproved Apps",
    content: "When you install a free PDF editor or a handy file converter you found online, you might be giving an unknown company access to your client documents. Using unapproved tools — called 'shadow IT' — is one of the most common ways SMEs accidentally leak data.",
    icon: "RefreshCw",
    tips: [
      "Enable automatic updates on all devices — most cyberattacks exploit known issues that patches already fix",
      "Before installing new software, check if your company has an approved alternative",
      "Free online tools (PDF converters, file compressors) often upload your files to their servers — read the fine print",
      "If you need a tool your company doesn't provide, ask IT rather than finding your own solution"
    ]
  },

  // ─── Block 4: Protecting Business Data ─────────────────────
  {
    title: "What Data Are You Handling?",
    content: "Not all data needs the same level of protection, but you need to recognize what you're working with. Client financial records, employee personal data, and strategic documents all require more care than a company newsletter. If you're unsure how sensitive something is, treat it as confidential.",
    icon: "ClipboardList",
    tips: [
      "Public: marketing materials, published content — can be shared freely",
      "Internal: meeting notes, project plans — keep within the company",
      "Confidential: client data, financials, HR records — share only on a need-to-know basis with secure methods",
      "Regulated: health data, payment card info — legal requirements apply; ask before handling"
    ]
  },
  {
    title: "Client Confidentiality in Practice",
    content: "Protecting client data isn't just good security — it's a professional obligation. Emailing an unencrypted spreadsheet of client financials, sharing your screen with a client file visible in the background, or leaving a printed report on your desk are all preventable mistakes.",
    icon: "Handshake",
    tips: [
      "Use encrypted file sharing (not email attachments) for sensitive client documents",
      "Before sharing your screen in a meeting, close unrelated tabs and documents",
      "Name files carefully — 'ClientName_Financials_2026.xlsx' on a shared drive is a risk if permissions are too broad",
      "Shred or securely delete physical and digital documents when they're no longer needed"
    ]
  },
  {
    title: "Backups & Ransomware",
    content: "Ransomware encrypts all your files and demands payment to unlock them. For an SME, this can mean losing years of client records, financial data, and project work overnight. The best defense isn't paying the ransom — it's having reliable backups that let you recover without them.",
    icon: "HardDrive",
    tips: [
      "Follow the 3-2-1 rule: 3 copies of important data, on 2 different types of storage, with 1 kept offline or offsite",
      "Set up automatic daily backups for critical business data",
      "Test your backups regularly — a backup you can't restore is not a backup",
      "Keep at least one backup disconnected from your network so ransomware can't reach it"
    ]
  },
  {
    title: "Privacy & Compliance Basics",
    content: "If your business collects client email addresses, stores employee records, or handles financial information, you likely have legal obligations under privacy regulations like the GDPR or Canada's Consumer Privacy Protection Act (CPPA). You don't need to be a lawyer, but you need to know the basics — because a breach notification to clients is both expensive and reputation-damaging.",
    icon: "Scale",
    tips: [
      "Only collect personal data you actually need — the less you store, the less you can lose",
      "Know where your client and employee data is stored and who has access to it",
      "If you suspect a data breach involving personal information, report it immediately — many regulations have strict notification timelines",
      "Review your privacy policy annually — it should reflect what you actually do with data"
    ]
  },

  // ─── Block 5: When Things Go Wrong ─────────────────────────
  {
    title: "Something Went Wrong — Now What?",
    content: "You clicked a suspicious link. You accidentally emailed a client file to the wrong person. You lost your phone with work email on it. Don't panic — but act quickly. The difference between a minor incident and a major breach is often measured in minutes, not days.",
    icon: "AlertTriangle",
    tips: [
      "Clicked a bad link? Disconnect from Wi-Fi, run a virus scan, change your passwords, and notify your IT contact",
      "Sent data to the wrong person? Contact them immediately to delete it, then report the incident internally",
      "Lost your phone? Use 'Find My Device' to lock or wipe it remotely, then change passwords for accounts on that phone",
      "When in doubt, report it — your IT team would rather hear about a false alarm than discover a breach weeks later"
    ]
  },
  {
    title: "Congratulations!",
    content: "You've completed the cybersecurity training. Security isn't about being perfect — it's about building habits that make you a harder target. The five most impactful things you can do today: enable MFA everywhere, use a password manager, verify before you trust, think before you share, and report anything suspicious.",
    icon: "Award",
    tips: [
      "Enable multi-factor authentication on your three most important accounts today",
      "Install a password manager this week and start migrating your passwords",
      "Share what you've learned with a colleague — security is a team effort",
      "When something feels off, trust your instincts and verify"
    ]
  }
];
